import { FadeInSection } from "@/components/company/company-animations";
import DataTableCompanies from "@/components/data-table-companies/data-table-companies";
import ErrorMessage from "@/components/error-message";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useApiStream } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import {
  CompanyDTO,
  SimilarsMode,
  SimilarsStreamEvent,
} from "@shaarp/common";
import { MapPinIcon, StarsIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCompanyTabContext } from "./company-tab-context";

export default function CompanySimilarsPage() {
  const apiStream = useApiStream();
  const { t } = useTranslation();
  const { company } = useCompanyTabContext();

  const [mode, setMode] = useState<SimilarsMode>("default");
  const [companies, setCompanies] = useState<CompanyDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const hasDepartment = Boolean(company.location?.address?.departmentCode);
  const isGeographicUnavailable = mode === "geographic" && !hasDepartment;

  const handleMessage = useCallback((event: { data: string }) => {
    try {
      const result: SimilarsStreamEvent = JSON.parse(event.data);

      switch (result.step) {
        case "companies":
          setCompanies((prev) => [...prev, ...result.companies]);
          break;
        case "finished":
          setIsLoading(false);
          break;
      }
    } catch {
      // Ignore malformed events
    }
  }, []);

  useEffect(() => {
    setCompanies([]);
    setError(null);

    if (isGeographicUnavailable) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    apiStream.post(
      `/search-engine/similars`,
      { companyId: company.id, mode },
      {
        onOpen: (controller) => {
          abortControllerRef.current = controller;
        },
        onMessage: handleMessage,
        onClose: () => {
          setIsLoading(false);
        },
        onError: (err) => {
          setError(err);
          setIsLoading(false);
        },
      },
    );

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [company.id, mode, isGeographicUnavailable]);

  if (error) {
    return <ErrorMessage />;
  }

  const emptyText = isGeographicUnavailable
    ? t("company.similars.noDepartment")
    : mode === "geographic"
      ? t("company.similars.noGeographicResults")
      : null;

  const modeFilters = (
    <Tabs
      value={mode}
      onValueChange={(value) => setMode(value as SimilarsMode)}
    >
      <TabsList>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <TabsTrigger value="default" className="gap-2">
                <StarsIcon className="w-4 h-4" />
                {t("company.similars.modes.default")}
              </TabsTrigger>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {t("company.similars.modes.defaultTooltip")}
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <TabsTrigger value="geographic" className="gap-2">
                <MapPinIcon className="w-4 h-4" />
                {t("company.similars.modes.geographic")}
              </TabsTrigger>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {t("company.similars.modes.geographicTooltip")}
          </TooltipContent>
        </Tooltip>
      </TabsList>
    </Tabs>
  );

  return (
    <FadeInSection>
      <DataTableCompanies
        companies={companies}
        pagination={false}
        isLoading={isLoading}
        emptyText={emptyText}
        columnsPersistenceKey="data-table-companies-similars"
        additionalFilters={modeFilters}
      />
    </FadeInSection>
  );
}
